import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import MenuIcon from "@mui/icons-material/Menu";
import {Box, Card, Container, Divider, IconButton, Paper, Tab, Tabs, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import {useSelector} from "react-redux";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate} from "react-router";
import {HashLink} from "react-router-hash-link";
import {getNotFoundPageInternalUrl} from "../../constants/appRoutes";
import Call from "../../hocs/call";
import useLanguages from "../../hooks/useLanguages";
import {ItemContainerTypes} from "../../model/item-containers-models/itemContainerType";
import {
  USER_ERRORS_NOT_ACCESSIBLE_CUSTOM_PAGE,
  USER_ERRORS_NOT_ACCESSIBLE_DASHBOARD
} from "../../translations/getManagedErrorsTranslations";
import CustomEmpty from "../custom-empty";
import Footer from "../footer";
import Header from "../header";
import ItemContainer from "../item-container";
import MaterialTree, {SINGLE_SELECT_MODE} from "../material-tree";
import Page from "../page";
import PageTitle from "../page-title";
import ItemContainerFilter, {FilterProvider} from "./ItemContainerFilters";
import {itemIsView} from "./itemsdefs";
import {appSelector} from "../../state/app/appSelectors";
import {hubSelector} from "../../state/hub/hubSelectors";
import {clearItemContainers, fetchItemContainerById} from "../../state/item-container/itemContainerActions";
import {itemContainerMapSelector} from "../../state/item-container/itemContainerSelectors";
import {nodeSelector} from "../../state/node/nodeSelectors";
import {showUserLoginForm} from "../../state/user/userActions";
import {userSelector} from "../../state/user/userSelectors";
import {getPageTitle} from "../../utils/other";
import {getIsUserAuthenticated} from "../../utils/permissions";
import "./style.css";

const handleHeight = (isSingleItemContainer: boolean) => {
  const ItemContainersBreadcrumbsHeight = document.getElementById("page-title__breadcrumbs")
    ? document.getElementById("page-title__breadcrumbs").offsetHeight
    : 0;
  const ItemContainersTitleHeight = document.getElementById("page-title__title")
    ? document.getElementById("page-title__title").offsetHeight
    : 0;
  const ItemContainersFiltersHeight = document.getElementById("itemContainers__filters")
    ? document.getElementById("itemContainers__filters").offsetHeight
    : 0;
  const ItemContainersHeaderHeight = document.getElementById("itemContainers__header")
    ? document.getElementById("itemContainers__header").offsetHeight
    : 0;

  if (!isSingleItemContainer) {
    const footerHeight = document.getElementById("footer") ? document.getElementById("footer").offsetHeight : 0;
    if (document.getElementById("itemContainers__container")) {
      document
        .getElementById("itemContainers__container")
        .setAttribute("style", `height: calc(100% - ${footerHeight + ItemContainersTitleHeight}px);`);
    }
    if (document.getElementById("itemContainers__content")) {
      document
        .getElementById("itemContainers__content")
        .setAttribute(
          "style",
          `height: calc(100% - ${
            ItemContainersBreadcrumbsHeight +
            ItemContainersTitleHeight +
            ItemContainersFiltersHeight +
            ItemContainersHeaderHeight
          }px);`
        );
    }
  }
};

const AccessibleLinks = ({nodeCode}) => {
  const node = useSelector(nodeSelector);
  const {t} = useLanguages();
  const location = useLocation();
  const {isA11y, language} = useSelector(appSelector);
  const params = new URLSearchParams(location.search);
  params.set("accessible", "true");
  const paramsStr = params.toString();
  const path = node?.nodeId && nodeCode ? `/${language}/${nodeCode.toLowerCase()}` : `/${language}`;
  return (
    <>
      {!isA11y && (
        <a href={path + (paramsStr.length > 0 ? "?" : "") + paramsStr} target="_self" className="skip-link sr-only">
          {t("commons.hashLinks.accessible")}
        </a>
      )}
      <HashLink to={{hash: "#main", search: location.search}} className="skip-link sr-only">
        {t("commons.hashLinks.main")}
      </HashLink>
      <HashLink to={{hash: "#footer", search: location.search}} className="skip-link sr-only">
        {t("commons.hashLinks.footer")}
      </HashLink>
    </>
  );
};

interface ItemContainersProps {
  itemContainerId?: number;
  nodeCode?: string;
  isDefault: boolean;
  hideBreadcrumbs: boolean;
  itemContainers: any;
  itemContainerType: string;
}

function ItemContainers({
  itemContainerId,
  nodeCode,
  isDefault,
  hideBreadcrumbs,
  itemContainers = [],
  itemContainerType
}: ItemContainersProps) {
  const {t, localizeI18nObj} = useLanguages();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const itemContainerMap = useSelector(itemContainerMapSelector);
  const user = useSelector(userSelector);
  const hub = useSelector(hubSelector);
  const node = useSelector(nodeSelector);
  const nodeId = node?.nodeId;

  const [tabIdx, setTabIdx] = useState<any>();
  const [menuOpened, setMenuOpened] = useState(false);
  const [currentItemContainerId, setCurrentItemContainerId] = useState<any>();

  const isSingleItemContainer: boolean = useMemo(
    () => itemContainerId !== null && itemContainerId !== undefined,
    [itemContainerId]
  );

  const isCategorized = useMemo(() => {
    return (
      !itemContainerId &&
      itemContainerType === ItemContainerTypes.Dashboard &&
      itemContainers &&
      itemContainers.categorizedDashboards &&
      itemContainers.categorizedDashboards.length > 0
    );
  }, [itemContainerId, itemContainers, itemContainerType]);

  const managedItemContainers = useMemo(() => {
    let obj = [];
    if (itemContainerType !== ItemContainerTypes.Dashboard) {
      obj = itemContainers;
    } else {
      if (itemContainers.uncategorizedDashboards && !isCategorized) {
        obj = itemContainers.uncategorizedDashboards;
      } else if (isCategorized) {
        obj = itemContainers.categorizedDashboards.reduce((newArray, c) => {
          newArray.push(...(c.dashboards || []));
          return newArray;
        }, []);
      }
    }
    return obj;
  }, [isCategorized, itemContainers, itemContainerType]);

  const hasGlobalFilter = useMemo(
    () =>
      (managedItemContainers || []).every(
        item =>
          !!item.filterId &&
          managedItemContainers[0]?.filterId === item.filterId &&
          _.isEqual(managedItemContainers[0].filterLevelsSelected, item.filterLevelsSelected)
      ),
    [managedItemContainers]
  );

  const categoryTree = useMemo(() => {
    const catTree =
      isCategorized && itemContainers
        ? itemContainers.categorizedDashboards.map((category, index) => {
            return {
              ...category,
              id: "cat_" + index,
              title: localizeI18nObj(category.title),
              isCategory: true,
              dashboards: (category.dashboards || []).map(dashboard => {
                return {
                  ...dashboard,
                  id: dashboard.id + "",
                  title: localizeI18nObj(dashboard.title)
                };
              })
            };
          })
        : [];

    return catTree;
  }, [isCategorized, itemContainers, localizeI18nObj]);

  const isAuthenticated = useMemo(() => getIsUserAuthenticated(user), [user]);

  const setTabIdxForCurrentItemContainerId = useCallback(
    id => {
      if (id) {
        const idx = managedItemContainers.findIndex(d => d.id === id);
        setCurrentItemContainerId(id);
        setTabIdx(idx);
      }
    },
    [managedItemContainers, setTabIdx, setCurrentItemContainerId]
  );

  useEffect(() => {
    handleHeight(isSingleItemContainer);
  });

  useEffect(() => {
    if (isCategorized) {
      setMenuOpened(itemContainers.expandCategoryTree);
    }
  }, [itemContainers, isCategorized]);

  useEffect(() => {
    if (itemContainerId) {
      setCurrentItemContainerId(itemContainerId);
    } else if (tabIdx === undefined || tabIdx === null) {
      if (itemContainers && itemContainers.defaultDashboardId) {
        setCurrentItemContainerId(itemContainers.defaultDashboardId);
        setTabIdxForCurrentItemContainerId(itemContainers.defaultDashboardId);
      } else if (managedItemContainers && managedItemContainers.length > 0) {
        setCurrentItemContainerId(managedItemContainers[0].id);
        setTabIdx(0);
      }

      if (!isCategorized && itemContainers && itemContainers.defaultDashboardId) {
        setTabIdxForCurrentItemContainerId(itemContainers.defaultDashboardId);
      }
    }
  }, [
    tabIdx,
    itemContainerId,
    itemContainers,
    isCategorized,
    managedItemContainers,
    setTabIdxForCurrentItemContainerId,
    setCurrentItemContainerId,
    setTabIdx
  ]);

  useEffect(() => {
    const f = () => handleHeight(isSingleItemContainer);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, [isSingleItemContainer]);

  const handleFetchItemContainer = (currentItemContainerId: number) =>
    dispatch(
      fetchItemContainerById(
        currentItemContainerId,
        itemContainerType === ItemContainerTypes.Dashboard ? "dashboard" : "customPages",
        true
      )
    );

  const itemContainer = useMemo(() => {
    const itemContainer = _.cloneDeep(itemContainerMap[currentItemContainerId]?.itemContainer);
    const viewsAdditionalInfo = _.cloneDeep(itemContainerMap[currentItemContainerId]?.viewsAdditionalInfo);
    if (itemContainer) {
      (itemContainer.item ?? []).forEach(row => {
        (row ?? []).forEach(col => {
          if (itemIsView(col)) {
            if (col?.viewTemplate?.packageId) {
              col.viewTemplate.packageId = col.viewTemplate.packageId.replace(/\+/g, ",");
            }
            if (col?.viewTemplate?.datasetId) {
              col.viewTemplate.datasetId = col.viewTemplate.datasetId.replace(/\+/g, ",");
            }
            if (viewsAdditionalInfo?.[col.id]) {
              col.viewTemplate = {
                ...col.viewTemplate,
                ...viewsAdditionalInfo[col.id]
              };
            }
          }
        });
      });
      return itemContainer;
    }
  }, [itemContainerMap, currentItemContainerId]);

  useEffect(() => {
    if (itemContainerMap[currentItemContainerId]?.error) {
      if (!isAuthenticated) {
        dispatch(
          showUserLoginForm(
            location.pathname.includes("dashboards")
              ? USER_ERRORS_NOT_ACCESSIBLE_DASHBOARD
              : USER_ERRORS_NOT_ACCESSIBLE_CUSTOM_PAGE,
            "warning",
            true
          )
        );
      } else {
        navigate(getNotFoundPageInternalUrl());
      }
    }
  }, [dispatch, navigate, location, itemContainerMap, currentItemContainerId, isAuthenticated]);

  useEffect(() => {
    return () => {
      dispatch(clearItemContainers());
    };
  }, [dispatch]);

  const isItemContainerTitleVisible = useMemo(() => {
    return itemContainerType === ItemContainerTypes.Dashboard || !itemContainer?.hideTitle;
  }, [itemContainer, itemContainerType]);

  return (
    <Call
      cb={handleFetchItemContainer}
      cbParam={currentItemContainerId}
      disabled={!currentItemContainerId || Boolean(itemContainer)}
    >
      <FilterProvider
        filterData={
          itemContainer?.filter
            ? {filter: itemContainer.filter, selectedLevels: itemContainer.filterLevelsSelected}
            : null
        }
      >
        <Fragment>
          <AccessibleLinks nodeCode={nodeCode} />
          <Page
            title={
              isSingleItemContainer
                ? itemContainer
                  ? getPageTitle([localizeI18nObj(itemContainer?.title), node?.name, hub?.name], t)
                  : getPageTitle([node?.name, hub?.name], t)
                : nodeId
                  ? itemContainerType === ItemContainerTypes.Dashboard
                    ? getPageTitle([t("scenes.itemContainers.dashboards.title", {name: node?.name}), hub?.name], t)
                    : getPageTitle([t("scenes.itemContainers.customPages.title", {name: node?.name}), hub?.name], t)
                  : itemContainerType === ItemContainerTypes.Dashboard
                    ? getPageTitle([t("scenes.itemContainers.dashboards.title", {name: hub?.name})], t)
                    : getPageTitle([t("scenes.itemContainers.customPages.title", {name: hub?.name})], t)
            }
          >
            <Header isDefault={isDefault} />
            <Box
              id="main"
              component="main"
              className="itemContainers"
              sx={{
                width: "100%",
                height: isSingleItemContainer ? "unset" : "100%",
                minHeight: "100%",
                backgroundColor: "#f5f5f5",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Container
                id="itemContainers__container"
                maxWidth={isSingleItemContainer ? undefined : false}
                sx={isSingleItemContainer ? undefined : {height: "100%"}}
              >
                <PageTitle
                  title={
                    itemContainerId || managedItemContainers.length === 1
                      ? localizeI18nObj(itemContainer?.title)
                      : itemContainerType === ItemContainerTypes.Dashboard
                        ? t("scenes.itemContainers.dashboards.title")
                        : t("scenes.itemContainers.customPages.title")
                  }
                  hideTitle={isCategorized || !isItemContainerTitleVisible}
                  breadcrumbItems={[
                    !itemContainer || itemContainerType === ItemContainerTypes.Dashboard
                      ? null
                      : {
                          label: localizeI18nObj(itemContainer?.title)
                        }
                  ]}
                  hideBreadcrumbs={hideBreadcrumbs}
                />
                {itemContainerMap[currentItemContainerId]?.error ? (
                  <CustomEmpty text={t("scenes.itemContainers.errors.requestError")} />
                ) : currentItemContainerId ? (
                  <Fragment>
                    {hasGlobalFilter && (
                      <Box
                        id="itemContainers__filters"
                        sx={{
                          padding: "0 20px"
                        }}
                      >
                        {itemContainer?.filter && <ItemContainerFilter itemContainer={itemContainer} />}
                      </Box>
                    )}
                    {(managedItemContainers || []).length > 1 && !isCategorized && (
                      <Box
                        id="itemContainers__header"
                        sx={{
                          padding: "8px 0"
                        }}
                      >
                        <Paper>
                          <Tabs
                            value={tabIdx}
                            variant="scrollable"
                            scrollButtons="auto"
                            onChange={(_, newValue) => {
                              setTabIdxForCurrentItemContainerId(managedItemContainers[newValue]?.id);
                            }}
                          >
                            {managedItemContainers.map(({id, title}, idx) => (
                              <Tab
                                key={idx}
                                label={localizeI18nObj(title)}
                                id={`tab__${id}`}
                                aria-controls={`itemContainer__${id}`}
                                tabIndex={0}
                              />
                            ))}
                          </Tabs>
                        </Paper>
                      </Box>
                    )}
                    <Box id="itemContainers__content" sx={{width: "100%", padding: "0 8px", display: "flex"}}>
                      {isCategorized && (
                        <Box id="itemContainers__content__menu" sx={{height: "100%"}}>
                          <Card sx={{height: "100%"}}>
                            <Box sx={{display: "flex", alignItems: "center", padding: "12px 12px 4px"}}>
                              {menuOpened && (
                                <Box sx={{paddingLeft: "8px", flexGrow: 1}}>
                                  <Typography
                                    sx={{
                                      fontSize: "28px",
                                      fontWeight: 300,
                                      letterSpacing: 0
                                    }}
                                  >
                                    {t("scenes.itemContainers.dashboards.title")}
                                  </Typography>
                                </Box>
                              )}
                              <div>
                                <Tooltip
                                  title={
                                    menuOpened
                                      ? t("scenes.itemContainers.dashboards.categoryTree.collapse.title")
                                      : t("scenes.itemContainers.dashboards.categoryTree.expand.title")
                                  }
                                >
                                  <span>
                                    <IconButton
                                      aria-label={
                                        menuOpened
                                          ? t("scenes.itemContainers.dashboards.categoryTree.collapse.ariaLabel")
                                          : t("scenes.itemContainers.dashboards.categoryTree.expand.ariaLabel")
                                      }
                                      onClick={() => setMenuOpened(!menuOpened)}
                                    >
                                      {menuOpened ? <ChevronLeftIcon /> : <MenuIcon />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </div>
                            </Box>
                            {menuOpened && (
                              <Box sx={{height: "100%", minWidth: "300px", width: "100%"}}>
                                <Divider sx={{margin: "8px"}} />
                                {(categoryTree || []).length > 0 && (
                                  <Box sx={{height: "100%", paddingLeft: "8px", paddingRight: "8px"}}>
                                    <MaterialTree
                                      treeData={categoryTree}
                                      idKey="id"
                                      childrenKey="dashboards"
                                      labelKey="title"
                                      renderNode={node => (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "4px 0"
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              height: "24px",
                                              width: "24px"
                                            }}
                                          >
                                            {node.isCategory ? (
                                              <FolderOpenIcon color="primary" />
                                            ) : (
                                              <DashboardIcon color="primary" />
                                            )}
                                          </Box>
                                          <Box
                                            sx={{
                                              marginLeft: "4px",
                                              textDecoration:
                                                !node.isCategory && node.id === `${currentItemContainerId}`
                                                  ? "underline"
                                                  : "none"
                                            }}
                                          >
                                            {`${node.title}`}
                                          </Box>
                                        </Box>
                                      )}
                                      selectMode={SINGLE_SELECT_MODE}
                                      disableSelection
                                      removeDisabledCheckbox
                                      defaultExpandedNodeIds={categoryTree.map(category => category.id)}
                                      onNodeClick={item =>
                                        item.isCategory ? undefined : setTabIdxForCurrentItemContainerId(+item.id)
                                      }
                                      hideButtonBar
                                      hideSearchBar
                                    />
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Card>
                        </Box>
                      )}
                      <Box
                        id="itemContainers__content__content"
                        sx={{
                          overflow: "auto",
                          width: "100%"
                        }}
                      >
                        {isCategorized && (
                          <Box
                            id="itemContainers__internalTitle"
                            sx={{
                              padding: "0 12px"
                            }}
                          >
                            <PageTitle
                              title={localizeI18nObj(itemContainer?.title) ?? ""}
                              breadcrumbItems={null}
                              hideBreadcrumbs
                            />
                          </Box>
                        )}
                        {!hasGlobalFilter && (
                          <Box
                            id="specificFilter__container"
                            sx={{
                              padding: "0 12px"
                            }}
                          >
                            {itemContainer?.filter && <ItemContainerFilter itemContainer={itemContainer} />}
                          </Box>
                        )}
                        <div
                          style={{width: "100%"}}
                          id={`itemContainer_${currentItemContainerId}`}
                          aria-labelledby={
                            managedItemContainers?.length > 1 && !isCategorized
                              ? `tab__${currentItemContainerId}`
                              : undefined
                          }
                        >
                          <ItemContainer itemContainer={itemContainer} />
                        </div>
                      </Box>
                    </Box>
                  </Fragment>
                ) : (
                  <CustomEmpty
                    text={
                      nodeId
                        ? itemContainerType === ItemContainerTypes.Dashboard
                          ? t("scenes.itemContainers.dashboards.empty.node")
                          : t("scenes.itemContainers.customPages.empty.node")
                        : itemContainerType === ItemContainerTypes.Dashboard
                          ? t("scenes.itemContainers.dashboards.empty.hub")
                          : t("scenes.itemContainers.customPages.empty.hub")
                    }
                  />
                )}
              </Container>
              <Footer />
            </Box>
          </Page>
        </Fragment>
      </FilterProvider>
    </Call>
  );
}

export default ItemContainers;
